import { useEffect, useRef, useState } from 'react';
import type { UpdateWelcomeBannerInput } from '@klaro7/validators';
import { Badge, Button, Input, Spinner } from '@klaro7/ui';

import { usePinGate } from '@/components/PinGate';
import {
  useWelcomeBannerSettings,
  useUpdateWelcomeBanner,
  useUploadWelcomeBannerImage,
} from '../hooks/useServicesSettings';

function extractErrorDetail(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = err as {
      response?: { data?: { error?: { message?: string }; message?: string }; status?: number; statusText?: string };
    };
    const apiMsg = resp.response?.data?.error?.message || resp.response?.data?.message;
    const status = resp.response?.status;
    if (apiMsg) return `[${status}] ${apiMsg}`;
    if (status) return `HTTP ${status}: ${resp.response?.statusText || 'Errore sconosciuto'}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Welcome banner configuration. Super-admin only — the parent gates rendering.
 * L'immagine intera caricata è ciò che vede il visitatore non registrato; tutto
 * il banner è cliccabile verso /register. reshowDays controlla la ricomparsa.
 */
export function WelcomeBannerSection() {
  const { requirePin, pinDialog } = usePinGate();
  const { data: settings, isLoading } = useWelcomeBannerSettings(true);
  const updateMutation = useUpdateWelcomeBanner();
  const uploadMutation = useUploadWelcomeBannerImage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [enabled, setEnabled] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [reshowDays, setReshowDays] = useState('1');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => {
    if (settings) {
      setEnabled(settings.enabled ?? false);
      setImageUrl(settings.imageUrl ?? '');
      setReshowDays(String(settings.reshowDays ?? 1));
    }
  }, [settings]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), toast.type === 'error' ? 15000 : 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadMutation.mutateAsync(file);
      setImageUrl(url);
      setToast({ type: 'success', message: 'Immagine caricata. Ricordati di salvare.' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore upload immagine: ${extractErrorDetail(err)}` });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleSave() {
    const days = Number(reshowDays);
    if (!Number.isInteger(days) || days < 0 || days > 365) {
      setToast({ type: 'error', message: 'Giorni re-show: intero tra 0 e 365.' });
      return;
    }
    const payload: UpdateWelcomeBannerInput = { enabled, imageUrl, reshowDays: days };
    try {
      await updateMutation.mutateAsync(payload);
      setToast({ type: 'success', message: 'Banner di benvenuto salvato con successo' });
    } catch (err) {
      setToast({ type: 'error', message: `Errore nel salvataggio: ${extractErrorDetail(err)}` });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-neutral-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 19.5h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5z" />
          </svg>
          <div>
            <h3 className="text-sm font-semibold text-neutral-700">Banner di benvenuto</h3>
            <p className="text-xs text-neutral-600">Pop-up mostrato ai visitatori non registrati (solo super admin)</p>
          </div>
        </div>
        <Badge variant={enabled ? 'success' : 'default'}>{enabled ? 'Attivo' : 'Disattivato'}</Badge>
      </div>

      <div className="space-y-4 p-5">
        {toast && (
          <div
            className={`rounded-lg border px-4 py-2.5 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {toast.message}
          </div>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal focus:ring-brand-teal"
          />
          Attivo (mostra il banner ai non registrati)
        </label>

        <div className="space-y-2">
          <p className="text-sm font-medium text-neutral-700">Immagine banner</p>
          {imageUrl ? (
            <div className="space-y-2">
              <img
                src={imageUrl}
                alt="Anteprima banner"
                className="max-h-48 w-full rounded-lg border border-neutral-200 object-contain"
              />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} loading={uploadMutation.isPending}>
                  Sostituisci immagine
                </Button>
                <Button variant="outline" size="sm" onClick={() => setImageUrl('')}>
                  Rimuovi
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} loading={uploadMutation.isPending}>
              Carica immagine
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
          <p className="text-xs text-neutral-500">
            JPG/PNG/GIF/WebP, max 2MB. Dimensione consigliata ~1500×460. Tutta l&apos;immagine sarà cliccabile verso la registrazione.
          </p>
        </div>

        <Input
          label="Giorni re-show"
          type="number"
          min={0}
          max={365}
          value={reshowDays}
          onChange={(e) => setReshowDays(e.target.value)}
          helperText="Ogni quanti giorni ri-mostrare dopo la chiusura. 1 = ogni giorno · 0 = una volta per sessione."
        />

        <div className="flex items-center justify-end border-t border-neutral-100 pt-4">
          {pinDialog}
          <Button onClick={() => requirePin(handleSave)} loading={updateMutation.isPending}>
            Salva banner
          </Button>
        </div>
      </div>
    </div>
  );
}
